/**
 * The project store: working state, undo/redo with labels, the revision log,
 * View versus Restore, export records, and autosave snapshots (plan §3.1).
 *
 * Framework-free: the UI subscribes and mirrors `state` into its own reactivity.
 */
import type { AutosaveSnapshot, BuildModel, ChangeKind, Design, ExportRecord, Fields, HistoryEntry, Revision, RevisionKind, Working } from './schemas';
import { AutosaveSnapshotSchema, SCHEMA_VERSION } from './schemas';
import { CONSTRUCTION, STOCK, type ConstructionProfile } from './profiles';
import { cloneDesign, createDesign, FAMILY, stockFor } from './design';
import { familyOf } from '../families/registry';
import { fitHistoryState } from '../families/repair-history';
import { contractOf } from '../families/contract';
import type { AnyStock } from '../families/stock';
import { classifyChange } from './changes';
import { changedFieldKeys, displayFor, editField, reconcileRepairs } from './fields';

const nowIso = () => new Date().toISOString();
const perfNow = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const deep = <T>(v: T): T => (v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T));

/** Undo entries kept in an autosave snapshot (the in-memory limit is `historyLimit`). */
export const SNAPSHOT_HISTORY_LIMIT = 50;

export interface StoreState {
  design: Design;
  fields: Fields;
  build: BuildModel;
  revisions: Revision[];
  currentRevisionId: string | null;
  exports: ExportRecord[];
  /** Revision id shown by View (non-destructive), or null. Any store change ends a view. */
  viewing: string | null;
  lastEditMs: number;
  lastChange: ChangeKind | null;
}

export type StoreEventType = 'apply' | 'undo' | 'redo' | 'set' | 'reset' | 'restore' | 'commit' | 'view' | 'export';
export interface StoreEvent {
  type: StoreEventType;
  label: string;
  change: ChangeKind;
  ms?: number;
  revision?: Revision;
  record?: ExportRecord;
}

export interface StoreInit {
  design?: Design;
  fields?: Fields;
  revisions?: Revision[];
  currentRevisionId?: string | null;
  exports?: ExportRecord[];
  historyLimit?: number;
  /** The stock profile as read from profiles/ (a maker's measured thickness); designs on other stock use theirs. */
  stock?: AnyStock;
  construction?: ConstructionProfile;
}

export type ApplyResult = Design | { design?: Design; fields?: Fields } | null | undefined | void;

export interface CommitOptions {
  kind?: RevisionKind;
  by?: string;
  note?: string;
  at?: string;
}

export type SnapshotCheck = { ok: true } | { ok: false; reason: string };

/** "r4" after r1–r3. */
export function nextRevisionId(revisions: readonly Pick<Revision, 'id'>[]): string {
  return `r${revisions.reduce((m, r) => Math.max(m, Number(String(r.id).slice(1)) || 0), 0) + 1}`;
}

const isDesignResult = (out: object): out is Design => 'W' in out && 'openings' in out;

// ---------------------------------------------------------------------------
// Snapshot validation (restore never throws on junk)
// ---------------------------------------------------------------------------

function validDesignShape(d: unknown): string | null {
  if (!d || typeof d !== 'object' || Array.isArray(d)) return 'The design is missing.';
  const o = d as Record<string, unknown>;
  for (const k of ['W', 'H', 'D']) if (!Number.isInteger(o[k])) return `The design has no valid ${k}.`;
  if (!Number.isInteger(o.n) || (o.n as number) < 0 || (o.n as number) > 50) return 'The design has no valid shelf count.';
  if (!Array.isArray(o.openings) || !o.openings.every(Number.isInteger)) return 'The design openings could not be read.';
  if (!o.appearance || typeof o.appearance !== 'object') return 'The design appearance could not be read.';
  if (fitHistoryState(o.params).kind === 'invalid') return 'The record of what the rebuild changed could not be read.';
  try {
    const d = { family: FAMILY, stockProfileId: STOCK.id, constructionProfileId: CONSTRUCTION.id, even: false, ...(o as Partial<Design>) } as Design;
    familyOf(d).generate(d);
  } catch {
    return 'The design could not be rebuilt.';
  }
  return null;
}

function validHistory(list: unknown): string | null {
  if (!Array.isArray(list)) return 'The undo history could not be read.';
  for (const e of list) {
    if (!e || typeof e !== 'object' || typeof (e as HistoryEntry).label !== 'string') return 'The undo history could not be read.';
    const d = (e as HistoryEntry).design;
    if (d !== undefined) {
      const bad = validDesignShape(d);
      if (bad) return `The undo history could not be read: ${bad}`;
    }
  }
  return null;
}

/** Validate an AutosaveSnapshot. Returns {ok:true} or {ok:false, reason}. Never throws. */
export function validateSnapshot(s: unknown): SnapshotCheck {
  try {
    if (!s || typeof s !== 'object' || Array.isArray(s)) return { ok: false, reason: 'The saved project is empty.' };
    const snap = s as Record<string, unknown>;
    if (snap.schemaVersion !== SCHEMA_VERSION) return { ok: false, reason: 'The saved project is from an unknown version.' };
    const st = snap.state as Record<string, unknown> | undefined;
    if (!st || typeof st !== 'object' || Array.isArray(st)) return { ok: false, reason: 'The saved project is empty.' };
    const bad = validDesignShape(st.design);
    if (bad) return { ok: false, reason: bad };
    if (!Array.isArray(st.revisions)) return { ok: false, reason: 'The revision list could not be read.' };
    const ids = new Set<string>();
    for (const r of st.revisions as unknown[]) {
      const rv = r as Partial<Revision> | null;
      if (!rv || typeof rv.id !== 'string' || typeof rv.manufacturingHash !== 'string') return { ok: false, reason: 'A revision could not be read.' };
      if (ids.has(rv.id)) return { ok: false, reason: `Revision ${rv.id} appears twice.` };
      ids.add(rv.id);
      const rb = validDesignShape(rv.design);
      if (rb) return { ok: false, reason: `Revision ${rv.id}: ${rb}` };
    }
    for (const list of [st.undoStack, st.redoStack]) {
      for (const e of Array.isArray(list) ? (list as { design?: unknown }[]) : []) {
        if (e?.design && fitHistoryState((e.design as { params?: unknown }).params).kind === 'invalid') return { ok: false, reason: 'An undo step’s record of what the rebuild changed could not be read.' };
      }
    }
    if (!st.fields || typeof st.fields !== 'object' || Array.isArray(st.fields)) return { ok: false, reason: 'The field provenance could not be read.' };
    if (st.currentRevisionId != null && typeof st.currentRevisionId !== 'string') return { ok: false, reason: 'The current revision could not be read.' };
    if (typeof st.currentRevisionId === 'string' && !ids.has(st.currentRevisionId)) return { ok: false, reason: 'The current revision is missing.' };
    const hu = validHistory(st.undoStack) ?? validHistory(st.redoStack);
    if (hu) return { ok: false, reason: hu };
    const z = AutosaveSnapshotSchema.safeParse(s);
    if (!z.success) {
      const issue = z.error.issues[0];
      return { ok: false, reason: `The saved project could not be read (${issue ? issue.path.join('.') || 'root' : 'unknown'}).` };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: 'The saved project could not be read.' };
  }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export type RevisionIdentity = 'current' | 'regenerated' | 'unchecked';

export interface Store {
  readonly state: StoreState;
  readonly design: Design;
  readonly build: BuildModel;
  readonly fields: Fields;
  readonly revisions: Revision[];
  readonly exports: ExportRecord[];
  readonly currentRevisionId: string | null;
  readonly currentRevision: Revision | null;
  readonly nextRevisionId: string;
  readonly lastEditMs: number;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly undoLabel: string;
  readonly redoLabel: string;
  /** The stock the current design is built from. */
  readonly stock: AnyStock;
  readonly construction: ConstructionProfile;
  readonly isDirty: boolean;
  readonly working: Working;
  apply(fn: (design: Design, state: StoreState) => ApplyResult, opts?: { label?: string; track?: boolean }): Store;
  undo(): Store;
  redo(): Store;
  set(patch: { design?: Design; fields?: Fields }, opts?: { label?: string }): Store;
  reset(init?: { design?: Design; fields?: Fields; revisions?: Revision[]; currentRevisionId?: string | null; exports?: ExportRecord[] }): Store;
  commitRevision(label?: string, opts?: CommitOptions): Revision;
  ensureRevision(label?: string, opts?: CommitOptions): Revision;
  revision(id: string): Revision | null;
  /**
   * How a saved revision regenerates with today's code (plan D10): `current` when its stored hashes
   * still match, `regenerated` when the generator now makes something different, `unchecked` for a
   * revision saved before assemblyHash existed whose manufacturing hash still matches.
   */
  revisionIdentity(id: string): RevisionIdentity | null;
  readonly viewing: string | null;
  readonly viewedRevision: Revision | null;
  readonly viewedBuild: BuildModel | null;
  view(id: string): boolean;
  backToLatest(): Store;
  restoreRevision(id: string, opts?: { label?: string }): boolean;
  recordExport(record: ExportRecord): Store;
  snapshot(opts?: { savedAt?: string }): AutosaveSnapshot;
  restore(s: unknown): Store | { ok: false; reason: string };
  subscribe(fn: (s: StoreState, e: StoreEvent) => void): () => void;
}

/**
 * createStore({design, fields, revisions, currentRevisionId, exports, historyLimit, stock, construction})
 * With no design the store starts from the Hylla preset with no fields and no revisions.
 */
export function createStore(init: StoreInit = {}): Store {
  const limit = init.historyLimit || 200;
  const stock = init.stock ?? STOCK;
  const construction = init.construction ?? CONSTRUCTION;
  const derive = (d: Design) => familyOf(d).generate(d, stock, construction);
  /** A pre-v2 revision's assembly identity under today's code (revisions are immutable, so cached per object). */
  const legacy = new WeakMap<Revision, string>();
  const legacyAssemblyHash = (r: Revision): string => {
    let h = legacy.get(r);
    if (!h) { h = contractOf(derive(r.design)).assemblyHash; legacy.set(r, h); }
    return h;
  };
  const classify = (a: Design, b: Design) => classifyChange(a, b, stock, construction);

  const revisions = deep(init.revisions ?? []);
  const design0 = cloneDesign(init.design ?? createDesign('hylla', stock, construction));
  let state: StoreState = {
    design: design0,
    fields: reconcileRepairs(deep(init.fields ?? {}), design0),
    build: derive(design0),
    revisions,
    currentRevisionId: init.currentRevisionId !== undefined ? init.currentRevisionId : (revisions.at(-1)?.id ?? null),
    exports: deep(init.exports ?? []),
    viewing: null,
    lastEditMs: 0,
    lastChange: null,
  };
  let past: HistoryEntry[] = [];
  let future: HistoryEntry[] = [];
  const subs = new Set<(s: StoreState, e: StoreEvent) => void>();
  const emit = (event: StoreEvent) => {
    for (const fn of subs) {
      try { fn(state, event); } catch (e) { console.error(e); }
    }
  };
  const snap = (label: string, change: ChangeKind): HistoryEntry => ({ label, change, design: state.design, fields: state.fields });

  function commit(next: Partial<StoreState>, event: StoreEvent) {
    const t0 = perfNow();
    const build = next.design ? derive(next.design) : state.build;
    const ms = perfNow() - t0;
    // a held repair follows the design however it arrives: apply, Restore, Undo, reset (plan D5)
    const fields = reconcileRepairs(next.fields ?? state.fields, next.design ?? state.design);
    state = { ...state, ...next, fields, build, viewing: null, lastEditMs: next.design ? ms : state.lastEditMs };
    emit({ ...event, ms });
  }

  const api: Store = {
    get state(): StoreState { return state; },
    get design(): Design { return state.design; },
    get build(): BuildModel { return state.build; },
    get fields(): Fields { return state.fields; },
    get revisions(): Revision[] { return state.revisions; },
    get exports(): ExportRecord[] { return state.exports; },
    get currentRevisionId(): string | null { return state.currentRevisionId; },
    get currentRevision(): Revision | null { return state.revisions.find((r) => r.id === state.currentRevisionId) ?? null; },
    get nextRevisionId(): string { return nextRevisionId(state.revisions); },
    get lastEditMs(): number { return state.lastEditMs; },
    get canUndo(): boolean { return past.length > 0; },
    get canRedo(): boolean { return future.length > 0; },
    get undoLabel(): string { return past.at(-1)?.label ?? ''; },
    get redoLabel(): string { return future.at(-1)?.label ?? ''; },
    get stock(): AnyStock { return stockFor(state.design, stock); },
    get construction(): ConstructionProfile { return construction; },
    /**
     * True when the working design differs from the current revision: its manufacturing
     * or appearance hash, or the layout (Even) setting. No current revision = dirty.
     */
    get isDirty(): boolean {
      const r = api.currentRevision;
      return !r || r.manufacturingHash !== state.build.manufacturingHash || r.appearanceHash !== state.build.appearanceHash || !!r.design.even !== !!state.design.even
        // support or interface changes can leave the manufacturing hash unchanged (D10); a revision saved before
        // assemblyHash is compared with its own design regenerated today, so user edits still count
        || (r.assemblyHash ?? legacyAssemblyHash(r)) !== contractOf(state.build).assemblyHash;
    },
    /** The "rN + edits" working state (test-plan §1 `Working`). */
    get working(): Working {
      return {
        parentRevisionId: state.currentRevisionId,
        design: state.design,
        fields: state.fields,
        dirty: api.isDirty,
        undoStack: past.map(({ label, change }) => ({ label, change })),
        redoStack: future.map(({ label, change }) => ({ label, change })),
      };
    },

    /**
     * Apply an undoable change. `fn(design, state)` returns either a new design
     * or a patch {design?, fields?}. Changed design fields are marked 'edited'
     * automatically unless {track:false} or the patch carries its own fields.
     */
    apply(fn: (design: Design, state: StoreState) => ApplyResult, { label = 'Edit', track = true }: { label?: string; track?: boolean } = {}) {
      const out = fn(cloneDesign(state.design), state);
      if (!out || typeof out !== 'object') return api;
      const patch: { design?: Design; fields?: Fields } = isDesignResult(out) ? { design: out } : out;
      const nextDesign = patch.design ?? state.design;
      let nextFields = patch.fields ?? state.fields;
      const change = classify(state.design, nextDesign);
      if (change === 'none' && !patch.fields) return api; // 'layout' (e.g. Even off) is recorded
      if (track && !patch.fields) {
        for (const k of changedFieldKeys(state.design, nextDesign)) {
          const [value, display] = displayFor(nextDesign, k);
          nextFields = editField(nextFields, k, value, { display });
        }
      }
      past.push(snap(label, change));
      if (past.length > limit) past.shift();
      future = [];
      commit({ design: nextDesign, fields: nextFields, lastChange: change }, { type: 'apply', label, change });
      return api;
    },
    undo() {
      const e = past.pop();
      if (!e || !e.design || !e.fields) return api;
      future.push(snap(e.label, e.change));
      const change = classify(state.design, e.design);
      commit({ design: e.design, fields: e.fields, lastChange: change }, { type: 'undo', label: e.label, change });
      return api;
    },
    redo() {
      const e = future.pop();
      if (!e || !e.design || !e.fields) return api;
      past.push(snap(e.label, e.change));
      const change = classify(state.design, e.design);
      commit({ design: e.design, fields: e.fields, lastChange: change }, { type: 'redo', label: e.label, change });
      return api;
    },
    /** Non-undoable state patch (e.g. fields from a new analysis). */
    set(patch: { design?: Design; fields?: Fields }, { label = 'Set' }: { label?: string } = {}) {
      const next: Partial<StoreState> = {};
      if (patch.design) next.design = cloneDesign(patch.design);
      if (patch.fields) next.fields = deep(patch.fields);
      commit(next, { type: 'set', label, change: next.design ? classify(state.design, next.design) : 'none' });
      return api;
    },
    /** Replace design/fields/revisions and clear history (e.g. after analysis or "New project"). */
    reset({ design, fields, revisions, currentRevisionId = null, exports }: { design?: Design; fields?: Fields; revisions?: Revision[]; currentRevisionId?: string | null; exports?: ExportRecord[] } = {}) {
      past = []; future = [];
      commit({
        design: cloneDesign(design ?? state.design), fields: deep(fields ?? {}),
        revisions: deep(revisions ?? []), currentRevisionId, exports: deep(exports ?? []),
      }, { type: 'reset', label: 'Reset', change: 'manufacturing' });
      return api;
    },
    /** Freeze the working design as a new revision (r4, r5 …) and make it current. */
    commitRevision(label = 'Revision', opts: CommitOptions = {}): Revision {
      const parent = api.currentRevision;
      let kind: RevisionKind;
      if (opts.kind) kind = opts.kind;
      else if (!parent) kind = 'manual';
      else {
        const c = classify(parent.design, state.design);
        kind = c === 'none' ? 'layout' : c;
      }
      const rev: Revision = {
        id: nextRevisionId(state.revisions), label, kind,
        design: cloneDesign(state.design), fields: deep(state.fields),
        manufacturingHash: state.build.manufacturingHash, appearanceHash: state.build.appearanceHash,
        assemblyHash: contractOf(state.build).assemblyHash,
        createdAt: opts.at ?? nowIso(), createdBy: opts.by ?? 'you',
        parentRevisionId: parent?.id ?? null,
        ...(opts.note ? { note: opts.note } : {}),
      };
      state = { ...state, revisions: [...state.revisions, rev], currentRevisionId: rev.id, viewing: null };
      emit({ type: 'commit', label, revision: rev, change: 'none' });
      return rev;
    },
    /** The current revision if the working design is unchanged since it, otherwise commit a new one. */
    ensureRevision(label = 'Revision', opts: CommitOptions = {}): Revision {
      return api.isDirty ? api.commitRevision(label, opts) : (api.currentRevision as Revision);
    },
    revision(id: string): Revision | null { return state.revisions.find((r) => r.id === id) ?? null; },
    revisionIdentity(id: string): RevisionIdentity | null {
      const r = api.revision(id);
      if (!r) return null;
      const b = derive(r.design);
      if (b.manufacturingHash !== r.manufacturingHash) return 'regenerated';
      if (!r.assemblyHash) return 'unchecked';
      return contractOf(b).assemblyHash === r.assemblyHash ? 'current' : 'regenerated';
    },

    // --- View (non-destructive) and Restore (undoable) ------------------------
    get viewing(): string | null { return state.viewing; },
    get viewedRevision(): Revision | null { return state.viewing ? api.revision(state.viewing) : null; },
    /** The build of the viewed revision, or null when not viewing. */
    get viewedBuild(): BuildModel | null {
      const r = api.viewedRevision;
      return r ? derive(r.design) : null;
    },
    /** Show a revision without changing anything ("Viewing rN · Not your current design"). */
    view(id: string): boolean {
      if (!api.revision(id)) return false;
      state = { ...state, viewing: id };
      emit({ type: 'view', label: `Viewing ${id}`, change: 'none' });
      return true;
    },
    backToLatest() {
      if (state.viewing === null) return api;
      state = { ...state, viewing: null };
      emit({ type: 'view', label: 'Back to latest', change: 'none' });
      return api;
    },
    /** Restore: an undoable edit that writes a revision's design into the working state. */
    restoreRevision(id: string, { label }: { label?: string } = {}): boolean {
      const r = api.revision(id);
      if (!r) return false;
      const before = state.design;
      api.apply(() => cloneDesign(r.design), { label: label ?? `Restored ${id}` });
      if (state.design === before) { state = { ...state, viewing: null }; emit({ type: 'view', label: 'Back to latest', change: 'none' }); }
      return true;
    },

    // --- Exports ----------------------------------------------------------------
    /** Append an ExportRecord (not undoable). */
    recordExport(record: ExportRecord) {
      state = { ...state, exports: [record, ...state.exports], viewing: null };
      emit({ type: 'export', label: `Exported ${record.kind}`, change: 'none', record });
      return api;
    },

    // --- Autosave -----------------------------------------------------------------
    /** Serialisable AutosaveSnapshot (undo history capped at SNAPSHOT_HISTORY_LIMIT entries). */
    snapshot({ savedAt = nowIso() }: { savedAt?: string } = {}): AutosaveSnapshot {
      return deep({
        schemaVersion: SCHEMA_VERSION,
        savedAt,
        state: {
          design: state.design, fields: state.fields, revisions: state.revisions, currentRevisionId: state.currentRevisionId,
          undoStack: past.slice(-SNAPSHOT_HISTORY_LIMIT), redoStack: future.slice(-SNAPSHOT_HISTORY_LIMIT), exports: state.exports,
        },
      });
    },
    /**
     * Restore an AutosaveSnapshot (reload). Returns the store on success; on an invalid
     * snapshot it changes nothing and returns {ok:false, reason} (it never throws).
     */
    restore(s: unknown) {
      const v = validateSnapshot(s);
      if (!v.ok) return { ok: false as const, reason: v.reason };
      const st = (s as AutosaveSnapshot).state;
      const keep = (list: HistoryEntry[]) => deep(list.filter((e) => e.design && e.fields));
      // prepare everything first; nothing in the store changes unless the whole restore succeeds
      const nextPast = keep(st.undoStack), nextFuture = keep(st.redoStack);
      const next = {
        design: cloneDesign(st.design), fields: deep(st.fields), revisions: deep(st.revisions),
        currentRevisionId: st.currentRevisionId ?? null, exports: deep(st.exports ?? []),
      };
      const before = { state, past, future };
      try {
        past = nextPast;
        future = nextFuture;
        commit(next, { type: 'restore', label: 'Restored', change: 'manufacturing' });
      } catch (e) {
        ({ state, past, future } = before);
        return { ok: false as const, reason: e instanceof Error ? e.message : 'The saved project could not be restored.' };
      }
      return api;
    },
    subscribe(fn: (s: StoreState, e: StoreEvent) => void): () => void {
      subs.add(fn);
      return () => { subs.delete(fn); };
    },
  };
  return api;
}
